import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaBuilding, FaHome, FaLandmark, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import aboutHero from '../assets/images/about-hero.jpg';

const About = () => {
  const [team, setTeamMembers] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/get_all_members`
        );
        const data = await response.json();
        setTeamMembers(data.data || []);
      } catch (error) {
        console.error("Error fetching team members:", error);
      }
    };

    fetchTeamMembers();
  }, []);

  return (
    <div className="min-h-screen bg-[#EDE9E0]">
      {/* Hero Section */}
      <div className="relative h-[50vh] bg-[#021437] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${aboutHero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#021437]/80 to-[#031D47]/90" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">About Dharaa Pariwar</h1>
          <p className="text-[#C7C091] text-xl md:text-2xl">
            Building trust, creating homes, and shaping futures since years
          </p>
        </div>
      </div>

      {/* Vision & Mission / Stats */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-[#2C3A2A] mb-6">Excellence in Real Estate</h2>
              <p className="text-[#385532] leading-relaxed text-lg">
                We are committed to providing exceptional real estate services with integrity, 
                expertise, and personalized attention to every client.
              </p>

              <div className="grid grid-cols-2 gap-6 mt-10">
                {[
                  { number: '1000+', label: 'Properties Managed' },
                  { number: '500+', label: 'Happy Clients' },
                  { number: '₹100Cr+', label: 'Property Value' },
                  { number: '20+', label: 'Years Experience' }
                ].map((stat, i) => (
                  <div key={i} className="bg-[#EDE9E0] p-6 rounded-3xl shadow-[8px_8px_20px_#d4c9b8,-8px_-8px_20px_#ffffff]">
                    <div className="text-4xl font-bold text-[#8F8431]">{stat.number}</div>
                    <div className="text-[#5F6A5A] mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { icon: FaHome, title: 'Residential Expertise' },
                { icon: FaLandmark, title: 'Commercial Solutions' },
                { icon: FaBuilding, title: 'Property Management' },
                { icon: FaMapMarkerAlt, title: 'Local Market Knowledge' }
              ].map((feature, i) => (
                <div key={i} className="bg-[#EDE9E0] p-8 rounded-3xl shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0]">
                  <feature.icon className="text-4xl text-[#8F8431] mb-4" />
                  <h3 className="font-semibold text-[#2C3A2A] text-xl">{feature.title}</h3>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-[#EDE9E0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3A2A]">Our Expert Team</h2>
            <p className="text-[#5F6A5A] mt-3 text-lg">Dedicated professionals committed to your success</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#EDE9E0] rounded-3xl overflow-hidden shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0] group"
              >
                <div className="h-80 overflow-hidden">
                  <img 
                    src={member.photo} 
                    alt={member.fullName} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                  />
                </div>
                <div className="p-8 text-center">
                  <h3 className="text-2xl font-semibold text-[#2C3A2A] mb-2">{member.fullName}</h3>
                  <p className="text-[#8F8431] font-medium">{member.designation}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Visit Us */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#EDE9E0] rounded-3xl p-8 md:p-12 shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] border border-[#E5E0D0]">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-[#2C3A2A] mb-8">Visit Our Office</h2>
                
                <div className="space-y-8">
                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0">
                      <FaMapMarkerAlt className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3A2A]">Office Address</h3>
                      <p className="text-[#385532] mt-1 leading-relaxed">
                        Shop No. 6, Milan Plaza,<br />
                        Lal Bahadur Shastri Marg,<br />
                        Civil Lines, Prayagraj, Uttar Pradesh – 211001
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0">
                      <FaPhone className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3A2A]">Phone Numbers</h3>
                      <p className="text-[#385532] mt-1">+91 63866 46807</p>
                    </div>
                  </div>

                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0">
                      <FaEnvelope className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3A2A]">Email</h3>
                      <p className="text-[#385532] mt-1 break-words">
                        dharaapariwar@gmail.com<br />
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-3xl overflow-hidden shadow-inner border border-[#D4C9A8] h-[380px]">
              <iframe
  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1801.627566092389!2d81.76728193837714!3d25.429732516729665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399acac5f8d8f8f%3A0x1234567890abcdef!2sShop%20No.%206%2C%20Milan%20Plaza%2C%20Civil%20Lines%2C%20Prayagraj!5e0!3m2!1sen!2sin!4v1743835419597!5m2!1sen!2sin"
  width="100%"
  height="100%"
  style={{ border: 0 }}
  allowFullScreen=""
  loading="lazy"
  referrerPolicy="no-referrer-when-downgrade"
/>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;